import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { PUBLIC_SITE_URL } from "../config";
import { PageHeader } from "../components/Layout";
import { useToast } from "../components/Toast";
import { Avatar, Badge, EmptyState, IconCalendar, IconExternal, IconUsers } from "../components/ui";
import { formatDate, formatDateTime, relativeTime } from "../utils/format";

export default function Overview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;

    api
      .get("/stats")
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) toast.error(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const t = data?.totals;
  const peakSignups = Math.max(1, ...(data?.signupTrend || []).map((d) => d.count));
  const topIndustryPeak = Math.max(1, ...(data?.topIndustries || []).map((i) => i.count));

  return (
    <>
      <PageHeader title="Overview" subtitle="How the Circle is doing right now">
        <a
          className="btn btn-sm"
          href={PUBLIC_SITE_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <IconExternal />
          View public site
        </a>
      </PageHeader>

      <div className="content">
        <div className="stat-grid">
          <Stat label="Members" value={t?.memberCount} loading={loading} tone="green"
            meta={t ? `+${t.newThisMonth.toLocaleString()} in the last 30 days` : ""} />
          <Stat label="Companies" value={t?.companyCount} loading={loading} tone="red"
            meta="Distinct organisations" />
          <Stat label="On the Spotlight" value={t?.featuredCount} loading={loading} tone="ink"
            meta="Featured on the public site" />
          <Stat label="Total RSVPs" value={t?.rsvpCount} loading={loading}
            meta={t ? `Across ${t.eventCount} event${t.eventCount === 1 ? "" : "s"}` : ""} />
        </div>

        <div className="two-col">
          <div className="stack">
            <section className="panel">
              <div className="panel-head">
                <h2>Newest members</h2>
                <span className="spacer" />
                <Link className="btn btn-sm btn-ghost" to="/members">
                  View all
                </Link>
              </div>
              <div className="panel-body">
                {loading ? (
                  <SkeletonList />
                ) : !data?.recentMembers?.length ? (
                  <EmptyState
                    icon={IconUsers}
                    title="No members yet"
                    message="Sign-ups from the public join form will land here."
                  />
                ) : (
                  data.recentMembers.map((m) => (
                    <div className="list-item" key={m.id}>
                      <Avatar name={m.name} id={m.id} />
                      <div className="list-item-body">
                        <div className="list-item-title">{m.name}</div>
                        <div className="list-item-meta">
                          {[m.designation, m.company].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      {m.source && m.source !== "joined" && <Badge>{m.source}</Badge>}
                      <span className="list-item-time">{relativeTime(m.created_at)}</span>
                    </div>
                  ))
                )}
              </div>
            </section>

            <section className="panel">
              <div className="panel-head">
                <h2>Sign-ups, last 30 days</h2>
              </div>
              <div className="panel-body">
                {loading ? (
                  <div className="skeleton" style={{ height: 46 }} />
                ) : !data?.signupTrend?.length ? (
                  <p className="cell-muted" style={{ fontSize: 13 }}>
                    No sign-ups in the last 30 days.
                  </p>
                ) : (
                  <>
                    <div className="sparkline">
                      {data.signupTrend.map((d) => (
                        <div
                          key={d.day}
                          className="sparkline-bar"
                          style={{ height: `${Math.max(4, (d.count / peakSignups) * 100)}%` }}
                          title={`${formatDate(d.day)} — ${d.count} sign-up${d.count === 1 ? "" : "s"}`}
                        />
                      ))}
                    </div>
                    <div className="row" style={{ marginTop: 10, justifyContent: "space-between" }}>
                      <span className="list-item-time">{formatDate(data.signupTrend[0].day)}</span>
                      <span className="list-item-time">
                        Peak {peakSignups}/day
                      </span>
                      <span className="list-item-time">
                        {formatDate(data.signupTrend[data.signupTrend.length - 1].day)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </section>
          </div>

          <div className="stack">
            <section className="panel">
              <div className="panel-head">
                <h2>Upcoming events</h2>
                <span className="spacer" />
                <Link className="btn btn-sm btn-ghost" to="/events">
                  Manage
                </Link>
              </div>
              <div className="panel-body">
                {loading ? (
                  <SkeletonList rows={3} />
                ) : !data?.upcomingEvents?.length ? (
                  <EmptyState
                    icon={IconCalendar}
                    title="Nothing scheduled"
                    message="Create an event and publish it to show it on the public site."
                    action={
                      <Link className="btn btn-primary btn-sm" to="/events">
                        Create an event
                      </Link>
                    }
                  />
                ) : (
                  data.upcomingEvents.map((e) => (
                    <div className="list-item" key={e.id}>
                      <div className="list-item-body">
                        <div className="list-item-title">{e.title}</div>
                        <div className="list-item-meta">
                          {formatDateTime(e.event_date)}
                          {e.location ? ` · ${e.location}` : ""}
                        </div>
                      </div>
                      {e.is_published ? (
                        <Badge tone="green">
                          <span className="badge-dot" />
                          Live
                        </Badge>
                      ) : (
                        <Badge tone="amber">Draft</Badge>
                      )}
                    </div>
                  ))
                )}
              </div>
            </section>

            <section className="panel">
              <div className="panel-head">
                <h2>Top industries</h2>
              </div>
              <div className="panel-body">
                {loading ? (
                  <SkeletonList rows={4} />
                ) : !data?.topIndustries?.length ? (
                  <p className="cell-muted" style={{ fontSize: 13 }}>
                    No industries recorded yet.
                  </p>
                ) : (
                  data.topIndustries.map((i) => (
                    <div className="bar-row" key={i.industry}>
                      <span className="bar-row-label" title={i.industry}>
                        {i.industry}
                      </span>
                      <span className="bar-track">
                        <span
                          className="bar-fill"
                          style={{ width: `${(i.count / topIndustryPeak) * 100}%` }}
                        />
                      </span>
                      <span className="bar-row-value">{i.count}</span>
                    </div>
                  ))
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}

function Stat({ label, value, meta, tone, loading }) {
  return (
    <div className={`stat ${tone ? `is-${tone}` : ""}`}>
      <div className="stat-label">{label}</div>
      {loading ? (
        <div className="skeleton" style={{ height: 30, width: "60%", margin: "8px 0 6px" }} />
      ) : (
        <div className="stat-value">{(value ?? 0).toLocaleString()}</div>
      )}
      {meta && <div className="stat-meta">{meta}</div>}
    </div>
  );
}

function SkeletonList({ rows = 5 }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <div className="list-item" key={i}>
          <div className="skeleton" style={{ width: 32, height: 32, borderRadius: "50%" }} />
          <div className="list-item-body">
            <div className="skeleton" style={{ width: "45%", marginBottom: 6 }} />
            <div className="skeleton" style={{ width: "65%", height: 10 }} />
          </div>
        </div>
      ))}
    </>
  );
}
